import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { 
  FiHeart, FiRefreshCw, FiCheck, FiAlertTriangle, FiX,
  FiClock, FiDatabase, FiZap, FiShield, FiExternalLink
} from 'react-icons/fi';

interface SystemHealth {
  cron_jobs: {
    trial_expiry: { last_run: string | null; status: string };
    usage_reset: { last_run: string | null; status: string };
  };
  webhooks: { events_24h: number; status: string };
  anomalies: { count_24h: number; status: string };
  database: { status: string; rls_enabled: boolean };
}

const SystemHealthPanel: React.FC = () => {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_get_system_health');
      if (error) throw error;
      if (data?.success) {
        setHealth(data);
      }
    } catch (err) {
      console.error('Error fetching system health:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const StatusIcon = ({ status }: { status: string }) => {
    switch (status) {
      case 'healthy':
        return <FiCheck className="text-green-400" />;
      case 'warning':
        return <FiAlertTriangle className="text-yellow-400" />;
      case 'critical':
      case 'error':
        return <FiX className="text-red-400" />;
      default:
        return <FiClock className="text-slate-400" />;
    }
  };

  const StatusBadge = ({ status }: { status: string }) => {
    const colors: Record<string, string> = {
      healthy: 'bg-green-500/20 text-green-400 border-green-500/30',
      warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      error: 'bg-red-500/20 text-red-400 border-red-500/30',
      idle: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
      pending: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };

    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded border ${colors[status] || colors.idle}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatLastRun = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    return 'Just now';
  };

  const HealthCard = ({ 
    title, 
    icon: Icon, 
    status, 
    detail, 
    subDetail,
    link
  }: { 
    title: string; 
    icon: React.ElementType; 
    status: string;
    detail?: string;
    subDetail?: string;
    link?: string;
  }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-slate-800/50 backdrop-blur-xl p-4 rounded-xl border border-slate-700/50 flex items-center justify-between"
    >
      <div className="flex items-center space-x-3">
        <div className="p-2 rounded-lg bg-slate-700/50">
          <Icon className="text-slate-300" />
        </div>
        <div>
          <p className="text-sm font-medium text-white">{title}</p>
          {detail && <p className="text-xs text-slate-400">{detail}</p>}
          {subDetail && <p className="text-xs text-slate-500">{subDetail}</p>}
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <StatusBadge status={status} />
        {link && (
          <a href={link} target="_blank" rel="noopener noreferrer" className="p-1 text-slate-400 hover:text-white">
            <FiExternalLink className="text-sm" />
          </a>
        )}
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-4" data-testid="system-health-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white flex items-center">
          <FiHeart className="mr-2 text-red-400" />
          System Health
        </h2>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={fetchHealth}
          disabled={loading}
          className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
        >
          <FiRefreshCw className={loading ? 'animate-spin' : ''} />
        </motion.button>
      </div>

      {/* Health Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* Stripe Connection */}
        <HealthCard
          title="Stripe"
          icon={FiZap}
          status="healthy"
          detail="Connected"
          link="https://dashboard.stripe.com"
        />

        {/* Coinbase Commerce */}
        <HealthCard
          title="Coinbase Commerce"
          icon={FiDatabase}
          status="healthy"
          detail="Ready"
        />

        {/* Database */}
        <HealthCard
          title="Database"
          icon={FiDatabase}
          status={health?.database?.status || 'healthy'}
          detail={health?.database?.rls_enabled ? 'RLS Enabled' : 'RLS Disabled'}
        />

        {/* Trial Expiry Cron */}
        <HealthCard
          title="Trial Expiry Job"
          icon={FiClock}
          status={health?.cron_jobs?.trial_expiry?.status || 'pending'}
          detail={`Last: ${formatLastRun(health?.cron_jobs?.trial_expiry?.last_run || null)}`}
          subDetail="Runs daily"
        />

        {/* Usage Reset Cron */}
        <HealthCard
          title="Usage Reset Job"
          icon={FiClock}
          status={health?.cron_jobs?.usage_reset?.status || 'pending'}
          detail={`Last: ${formatLastRun(health?.cron_jobs?.usage_reset?.last_run || null)}`}
          subDetail="Runs monthly"
        />

        {/* Webhook Health */}
        <HealthCard
          title="Webhooks"
          icon={FiZap}
          status={health?.webhooks?.status || 'idle'}
          detail={`${health?.webhooks?.events_24h || 0} events (24h)`}
        />

        {/* Anomaly Monitor */}
        <HealthCard
          title="Anomaly Detection"
          icon={FiShield}
          status={health?.anomalies?.status || 'healthy'}
          detail={`${health?.anomalies?.count_24h || 0} anomalies (24h)`}
        />
      </div>

      {/* Overall Status */}
      <div className="p-4 bg-gradient-to-r from-slate-800/80 to-slate-900/50 rounded-xl border border-slate-700/50">
        <div className="flex items-center space-x-3">
          <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse" />
          <span className="text-sm font-medium text-white">All systems operational</span>
          <span className="text-xs text-slate-500">• Auto-refresh every 30s</span>
        </div>
      </div>
    </div>
  );
};

export default SystemHealthPanel;